/**
 * The client's view of the server clock. Snapshots arrive on the "received" timeline; the
 * opponent is drawn a fixed delay behind it, and the own prediction runs ahead of it by one
 * round trip plus a margin so inputs reach the server before it simulates their tick. Pure:
 * every method takes the current time, so tests drive it with synthetic clocks.
 */
import { NET, TICK_MS } from './constants.js';
/** Clamps `v` to [lo, hi]. */
function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
/** Assumed round trip until the first echo arrives, so the first inputs are not all late. */
export const INITIAL_RTT_GUESS_MS = 100;
export class NetClock {
    /** Smoothed round trip and its jitter, from the input echo. */
    rttMs = INITIAL_RTT_GUESS_MS;
    jitterMs = 0;
    samples = 0;
    /** Extra lead in ticks added after late inputs; decays back to one. */
    marginTicks = 1;
    /** Received timeline: server tick minus client time in ticks, smoothed. */
    offsetTicks = 0;
    anchored = false;
    lastLateMs = -Infinity;
    lastRaiseMs = -Infinity;
    newestTick = -1;
    get isAnchored() {
        return this.anchored;
    }
    /** A round trip measured before any input echo (the join handshake) replaces the guess. */
    seedRtt(rttMs) {
        if (this.samples === 0 && Number.isFinite(rttMs) && rttMs >= 0)
            this.rttMs = rttMs;
    }
    /** Records a snapshot's arrival, the echoed round trip (if any) and the late-input count. */
    onSnapshot(tick, nowMs, rttSampleMs, late) {
        if (rttSampleMs !== null && Number.isFinite(rttSampleMs) && rttSampleMs >= 0) {
            if (this.samples === 0) {
                this.rttMs = rttSampleMs;
                this.jitterMs = 0;
            }
            else {
                this.jitterMs += 0.1 * (Math.abs(rttSampleMs - this.rttMs) - this.jitterMs);
                this.rttMs += 0.1 * (rttSampleMs - this.rttMs);
            }
            this.samples++;
        }
        if (tick > this.newestTick) {
            this.newestTick = tick;
            const estimate = tick - nowMs / TICK_MS;
            // A snapshot that arrives earlier than expected is the truer anchor; late ones only drag
            // the estimate down slowly, so a single delayed packet does not stall the timeline.
            this.offsetTicks = !this.anchored
                ? estimate
                : estimate > this.offsetTicks
                    ? estimate
                    : this.offsetTicks + 0.05 * (estimate - this.offsetTicks);
            this.anchored = true;
        }
        if (late > 0) {
            // One step per round trip: a raise takes that long to show in the server's reports.
            if (nowMs - this.lastRaiseMs > this.rttMs + 2 * TICK_MS) {
                this.marginTicks = Math.min(6, this.marginTicks + 1);
                this.lastRaiseMs = nowMs;
            }
            this.lastLateMs = nowMs;
        }
        else if (this.marginTicks > 1 && nowMs - this.lastLateMs > 3000) {
            this.marginTicks -= 1;
            this.lastLateMs = nowMs;
        }
    }
    /** Resets the timeline anchor (after a pause or a full resync) but keeps the RTT estimate. */
    reanchor() {
        this.anchored = false;
        this.newestTick = -1;
    }
    /** The newest tick the client can expect to hold right now (fractional). */
    receivedTick(nowMs) {
        return this.offsetTicks + nowMs / TICK_MS;
    }
    /** Where the server itself is about now: one way trip past the received timeline. */
    serverTickNow(nowMs) {
        return this.receivedTick(nowMs) + this.rttMs / 2 / TICK_MS;
    }
    /** The fractional tick the opponent is drawn at. */
    renderTick(nowMs) {
        return this.receivedTick(nowMs) - NET.INTERP_DELAY_TICKS;
    }
    /** Lead of the own prediction over the received timeline, in ticks. */
    leadTicks() {
        const lead = Math.ceil((this.rttMs + 2 * this.jitterMs) / TICK_MS) + this.marginTicks;
        return clamp(lead, NET.MIN_LEAD_TICKS, NET.MAX_LEAD_TICKS);
    }
    /** The tick the client should be predicting right now. */
    targetPredictedTick(nowMs) {
        return Math.round(this.receivedTick(nowMs)) + this.leadTicks();
    }
    /**
     * Time-dilation factor for the fixed-step accumulator: run slightly fast while behind the
     * target, slightly slow while ahead. Large gaps are for the caller to close with a jump.
     */
    rateScale(predictedTick, nowMs) {
        const error = this.targetPredictedTick(nowMs) - predictedTick;
        return clamp(1 + error * 0.1, 0.85, 1.3);
    }
}
//# sourceMappingURL=clock.js.map